import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';

interface MaintenanceProps {
  onRetry?: () => void;
}

export const MaintenancePage: React.FC<MaintenanceProps> = ({ onRetry }) => {
  const [email, setEmail] = useState('');
  const [notified, setNotified] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [checkStatusText, setCheckStatusText] = useState<string | null>(null);

  // Countdown timer simulation (or real target)
  const [timeLeft, setTimeLeft] = useState({
    hours: 2,
    minutes: 45,
    seconds: 30
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
        }
        return prev;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleNotifySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;
    setNotified(true);
  };

  const handleCheckConnection = () => {
    setIsChecking(true);
    setCheckStatusText('Verificando status dos servidores Cardumy...');

    setTimeout(() => {
      // Simulate probe
      setIsChecking(false);
      setCheckStatusText('Servidores ainda em procedimento de manutenção. Tente novamente em alguns instantes.');
      if (onRetry) onRetry();
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-[#050811] text-white selection:bg-purple-500/30 flex flex-col items-center justify-between p-4 sm:p-6 lg:p-10 relative overflow-hidden font-sans">
      {/* Background Oceanic Spotlight Glows */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-gradient-to-b from-sky-500/15 via-purple-600/10 to-transparent rounded-full blur-[140px]" />
        <div className="absolute -bottom-20 right-1/4 w-[500px] h-[400px] bg-indigo-900/15 rounded-full blur-[150px]" />
      </div>

      {/* Top Header / Brand */}
      <header className="relative z-10 w-full max-w-5xl flex items-center justify-between py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-lg shadow-purple-600/30">
            <i className="fas fa-fish-fins text-white text-lg"></i>
          </div>
          <span className="text-xl font-black tracking-tight text-white">Cardumy</span>
        </div>

        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-950/40 border border-amber-500/30 text-amber-400 text-[10px] font-black uppercase tracking-widest">
          <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping"></span>
          <span>Manutenção Programada</span>
        </div>
      </header>

      {/* Main Center Container */}
      <main className="relative z-10 w-full max-w-3xl my-auto py-12 text-center flex flex-col items-center">
        
        {/* Animated Sonar / Radar Icon */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="relative mb-8"
        >
          {/* Pulsing rings */}
          <div className="absolute inset-0 -m-6 rounded-full border border-sky-500/20 animate-ping [animation-duration:3s]"></div>
          <div className="absolute inset-0 -m-12 rounded-full border border-purple-500/10 animate-pulse"></div>

          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl bg-gradient-to-br from-[#121933] to-[#0a0f20] border border-sky-500/30 flex items-center justify-center shadow-2xl shadow-sky-950/60 relative">
            <i className="fas fa-screwdriver-wrench text-3xl sm:text-4xl text-transparent bg-clip-text bg-gradient-to-r from-sky-400 via-purple-400 to-pink-400"></i>
          </div>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-3xl sm:text-5xl md:text-6xl font-black tracking-tight leading-tight mb-4"
        >
          Aprofundando as águas do <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-sky-400">Cardumy</span>
        </motion.h1>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-slate-300 text-sm sm:text-base max-w-xl mx-auto leading-relaxed mb-8 font-medium"
        >
          Estamos realizando uma manutenção preventiva e atualizando a infraestrutura de coleções, catálogo de cartas e servidores de torneios para trazer uma navegação ainda mais rápida.
        </motion.p>

        {/* Countdown Box */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="w-full max-w-md bg-[#090e1c]/90 border border-slate-800/80 rounded-2xl p-6 mb-8 shadow-xl backdrop-blur-md"
        >
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-4 flex items-center justify-center gap-2">
            <i className="fas fa-clock text-purple-400"></i>
            <span>Previsão de Retorno dos Sistemas</span>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-xl bg-slate-950/60 border border-white/5">
              <span className="text-2xl sm:text-3xl font-black text-white block">
                {String(timeLeft.hours).padStart(2, '0')}
              </span>
              <span className="text-[9px] uppercase font-bold text-slate-500 tracking-wider">Horas</span>
            </div>
            <div className="p-3 rounded-xl bg-slate-950/60 border border-white/5">
              <span className="text-2xl sm:text-3xl font-black text-white block">
                {String(timeLeft.minutes).padStart(2, '0')}
              </span>
              <span className="text-[9px] uppercase font-bold text-slate-500 tracking-wider">Minutos</span>
            </div>
            <div className="p-3 rounded-xl bg-slate-950/60 border border-white/5">
              <span className="text-2xl sm:text-3xl font-black text-sky-400 block">
                {String(timeLeft.seconds).padStart(2, '0')}
              </span>
              <span className="text-[9px] uppercase font-bold text-slate-500 tracking-wider">Segundos</span>
            </div>
          </div>
        </motion.div>

        {/* System Checklist Indicators */}
        <div className="w-full max-w-md grid grid-cols-2 gap-2.5 mb-8 text-left">
          <div className="p-3 rounded-xl bg-[#080c18]/80 border border-slate-800/60 flex items-center justify-between text-xs">
            <span className="text-slate-400 text-[11px] font-medium">Catálogo de Cartas</span>
            <span className="text-emerald-400 text-[10px] font-bold flex items-center gap-1">
              <i className="fas fa-check-circle"></i> Atualizado
            </span>
          </div>
          <div className="p-3 rounded-xl bg-[#080c18]/80 border border-slate-800/60 flex items-center justify-between text-xs">
            <span className="text-slate-400 text-[11px] font-medium">Bases de Preço TCG</span>
            <span className="text-amber-400 text-[10px] font-bold flex items-center gap-1">
              <i className="fas fa-arrows-rotate fa-spin"></i> Sincronizando
            </span>
          </div>
          <div className="p-3 rounded-xl bg-[#080c18]/80 border border-slate-800/60 flex items-center justify-between text-xs">
            <span className="text-slate-400 text-[11px] font-medium">Motor de Torneios</span>
            <span className="text-amber-400 text-[10px] font-bold flex items-center gap-1">
              <i className="fas fa-arrows-rotate fa-spin"></i> Otimizando
            </span>
          </div>
          <div className="p-3 rounded-xl bg-[#080c18]/80 border border-slate-800/60 flex items-center justify-between text-xs">
            <span className="text-slate-400 text-[11px] font-medium">Segurança & Auth</span>
            <span className="text-emerald-400 text-[10px] font-bold flex items-center gap-1">
              <i className="fas fa-shield-check"></i> Protegido
            </span>
          </div>
        </div>

        {/* Notify me Form */}
        <div className="w-full max-w-md mb-8">
          {!notified ? (
            <form onSubmit={handleNotifySubmit} className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Avise-me no meu e-mail..."
                required
                className="flex-1 px-5 py-3 rounded-xl bg-[#0c1224] border border-slate-800 text-white placeholder:text-slate-600 text-xs sm:text-sm focus:outline-none focus:border-purple-500 transition-all"
              />
              <button
                type="submit"
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold text-xs uppercase tracking-wider hover:opacity-90 transition-all shadow-lg shadow-purple-600/20 shrink-0 cursor-pointer"
              >
                Notificar
              </button>
            </form>
          ) : (
            <div className="p-4 rounded-xl bg-emerald-950/40 border border-emerald-500/30 text-emerald-400 text-xs font-bold flex items-center justify-center gap-2">
              <i className="fas fa-circle-check"></i>
              <span>Você será avisado assim que os portões forem reabertos!</span>
            </div>
          )}
        </div>

        {/* Retry Button */}
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <button
            onClick={handleCheckConnection}
            disabled={isChecking}
            className="px-6 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-slate-200 border border-slate-700/60 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-all cursor-pointer disabled:opacity-50"
          >
            <i className={`fas fa-rotate-right ${isChecking ? 'fa-spin text-purple-400' : ''}`}></i>
            <span>{isChecking ? 'Verificando...' : 'Verificar Conexão'}</span>
          </button>

          <Link
            to="/"
            className="text-xs text-slate-400 hover:text-white transition-colors underline py-2"
          >
            Ir para Página Inicial
          </Link>
        </div>

        {checkStatusText && (
          <motion.p
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-[11px] text-amber-400/90 font-medium mt-3"
          >
            {checkStatusText}
          </motion.p>
        )}

      </main>

      {/* Footer & Social Channels */}
      <footer className="relative z-10 w-full max-w-5xl flex flex-col sm:flex-row items-center justify-between gap-4 py-4 border-t border-slate-800/40 text-xs text-slate-500">
        <p>© 2025 Cardumy. Todos os direitos reservados.</p>
        
        <div className="flex items-center gap-5 text-slate-400">
          <span className="text-[11px] text-slate-500">Acompanhe atualizações em tempo real:</span>
          <a href="https://discord.gg" target="_blank" rel="noreferrer" className="hover:text-indigo-400 transition-colors" title="Discord">
            <i className="fab fa-discord"></i>
          </a>
          <a href="https://instagram.com" target="_blank" rel="noreferrer" className="hover:text-pink-400 transition-colors" title="Instagram">
            <i className="fab fa-instagram"></i>
          </a>
          <a href="https://whatsapp.com" target="_blank" rel="noreferrer" className="hover:text-emerald-400 transition-colors" title="WhatsApp">
            <i className="fab fa-whatsapp"></i>
          </a>
        </div>
      </footer>
    </div>
  );
};
